import { clients, FeatureFlag, FeatureFlagCheckData } from '@jobilla/nexus-sdk'
import {
	EvaluationContext,
	JsonValue,
	Logger,
	Provider,
	ProviderMetadata,
	ResolutionDetails,
} from '@openfeature/react-sdk'

export class NexusOpenFeatureProvider implements Provider {
	public readonly runsOn = 'client'

	private _features: Map<FeatureFlag, boolean> = new Map()

	readonly metadata: ProviderMetadata = {
		name: 'nexus-openfeature-provider',
	}

	async initialize(context: EvaluationContext): Promise<void> {
		this._features = new Map()

		if (context.targetingKey) {
			try {
				const response = await clients.featureFlagList()

				response.data.features.forEach((feature: FeatureFlagCheckData) => {
					this._features.set(feature.feature, feature.enabled)
				})
			} catch (e) {
				this._features = new Map()

				throw e
			}
		}
	}

	async onContextChange(oldContext: EvaluationContext, newContext: EvaluationContext): Promise<void> {
		// Nexus evaluates flags for the organisation in the access token, so a switched
		// organisation needs a refetch even for the same user.
		if (
			oldContext.targetingKey !== newContext.targetingKey
			|| oldContext.organisation !== newContext.organisation
		) {
			return this.initialize(newContext)
		}
	}

	resolveBooleanEvaluation(
		flagKey: FeatureFlag,
		defaultValue: boolean,
		_context: EvaluationContext,
		_logger: Logger,
	): ResolutionDetails<boolean> {
		return {
			value: this._features.get(flagKey) ?? defaultValue,
		}
	}

	resolveNumberEvaluation(
		_flagKey: FeatureFlag,
		_defaultValue: number,
		_context: EvaluationContext,
		logger: Logger,
	): ResolutionDetails<number> {
		logger.warn('Number based Feature Flag evaluation has not been implemented yet.')
		throw new Error('Not yet implemented')
	}

	resolveObjectEvaluation<T extends JsonValue>(
		_flagKey: FeatureFlag,
		_defaultValue: T,
		_context: EvaluationContext,
		logger: Logger,
	): ResolutionDetails<T> {
		logger.warn('Object based Feature Flag evaluation has not been implemented yet.')
		throw new Error('Not yet implemented')
	}

	resolveStringEvaluation(
		_flagKey: FeatureFlag,
		_defaultValue: string,
		_context: EvaluationContext,
		logger: Logger,
	): ResolutionDetails<string> {
		logger.warn('String based Feature Flag evaluation has not been implemented yet.')
		throw new Error('Not yet implemented')
	}
}
